import { useParams, Link, useNavigate } from 'react-router-dom'
import { useChurch, useChurchEvents, useClaimChurch } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Button } from '../components/ui/Button'
import { Avatar } from '../components/ui/Avatar'
import { StatusPill } from '../components/ui/StatusPill'
import { EventTypePill } from '../components/ui/EventTypePill'
import { ChurchLocationMap } from '../components/ChurchLocationMap'
import { track } from '../lib/analytics'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'

function denominationLabel(key: string | null) {
  if (!key) return null
  return DENOMINATION_OPTIONS.find((d) => d.key === key)?.label ?? null
}

/** Strip a trailing " Church" so e.g. "Emmaus Baptist Church" → "Emmaus Baptist". */
function shortName(name: string) {
  return name.replace(/\s+church$/i, '').trim() || name
}

function formatEventDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function formatEventTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function ClaimChurchPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: churchData, isLoading } = useChurch(id)
  const { user } = useAuthStore()
  const claim = useClaimChurch(id ?? '')

  const church = churchData?.data
  const { data: eventsData, isLoading: eventsLoading } = useChurchEvents(church?.slug)
  const occurrences = eventsData?.data ?? []

  const handleClaim = () => {
    claim.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === 'claimed') {
          track('church_claim', { church_id: id, slug: result.church.slug })
          navigate(`/church-onboarding/${result.church.slug}`)
        }
      },
    })
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!church) {
    return (
      <PublicLayout>
        <div
          className="flex items-center justify-center flex-col gap-3"
          style={{ height: 'calc(100vh - 80px)' }}
        >
          <p className="text-sm text-ink-2">Church not found.</p>
          <Link to="/churches" className="text-primary text-[13px] no-underline font-medium">
            ← Browse churches
          </Link>
        </div>
      </PublicLayout>
    )
  }

  if (church.claimed) {
    return (
      <PublicLayout>
        <div
          className="flex items-center justify-center flex-col gap-4 px-6 text-center"
          style={{ height: 'calc(100vh - 80px)' }}
        >
          <div className="text-[40px]">🏛️</div>
          <p className="serif text-2xl m-0">This church has already been claimed.</p>
          <Link
            to={`/churches/${church.slug}`}
            className="text-primary text-sm no-underline font-medium"
          >
            View profile →
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const churchName = church.name
  const churchShort = shortName(church.name)
  const churchLocation = [church.city, church.state].filter(Boolean).join(', ')
  const churchDenom = denominationLabel(church.denomination)
  // Prefer the real fetched list once it lands; fall back to the server-computed
  // count while it's still loading so the meta line doesn't flash "0" first.
  const upcomingCount = eventsLoading ? church.upcomingEventCount : occurrences.length
  const metaLine = [
    churchLocation,
    churchDenom,
    `${upcomingCount} upcoming event${upcomingCount === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ')
  const statusLine = [churchLocation, churchDenom].filter(Boolean).join(' · ')

  const claimSucceededPending = claim.isSuccess && claim.data.status === 'pending'

  return (
    <PublicLayout>
      <div
        className="onb"
        style={{ background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-sans)' }}
      >
        <div className="onb-claim onb-pad" style={{ padding: '52px 56px 40px' }}>
          {/* ── LEFT: the action ─────────────────────────────────────────── */}
          <div style={{ position: 'relative' }}>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: -40,
                left: -30,
                width: 380,
                height: 380,
                borderRadius: 999,
                background: 'radial-gradient(circle, rgba(124,92,211,.14), transparent 68%)',
                filter: 'blur(8px)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--uv-claim-ink)',
                  background: 'var(--uv-claim-bg)',
                  border: '1px solid #f3e3b8',
                  padding: '7px 13px',
                  borderRadius: 999,
                  marginBottom: 22,
                }}
              >
                <span
                  style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--warning)' }}
                />
                Unclaimed listing
              </span>
              <h1
                className="onb-h1"
                style={{
                  fontSize: 46,
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  fontWeight: 700,
                  margin: '0 0 14px',
                }}
              >
                Claim {churchName}
              </h1>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  marginBottom: 20,
                  color: 'var(--ink-2)',
                  fontSize: 15,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50% 50% 50% 0',
                    transform: 'rotate(-45deg)',
                    background: 'var(--brand-gradient)',
                    flex: '0 0 auto',
                  }}
                />
                {statusLine}
              </div>
              <p
                style={{
                  fontSize: 17,
                  lineHeight: 1.6,
                  color: 'var(--ink-2)',
                  margin: '0 0 28px',
                  maxWidth: 440,
                }}
              >
                We found your church online and built this listing so neighbors can discover your
                events. Claim it to make it yours — and everything to the right becomes yours to
                manage.
              </p>

              {/* Claim card */}
              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-lifted)',
                  padding: 28,
                }}
              >
                {claimSucceededPending ? (
                  <div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        marginBottom: 10,
                      }}
                    >
                      Verification pending
                    </div>
                    <p
                      style={{
                        fontSize: 14,
                        color: 'var(--ink-2)',
                        margin: '0 0 14px',
                        lineHeight: 1.6,
                      }}
                    >
                      We couldn't automatically verify your connection to {churchName}. Our team
                      will review your request within 1–2 business days — you'll get an email when
                      it's approved.
                    </p>
                    <p
                      style={{
                        fontSize: 14,
                        color: 'var(--ink-2)',
                        margin: '0 0 20px',
                        lineHeight: 1.6,
                      }}
                    >
                      In the meantime, you can set up your profile and subscribe.{' '}
                      <strong>Posting events is unlocked once your page is verified.</strong>
                    </p>
                    <Button
                      size="lg"
                      className="w-full"
                      onClick={() => navigate(`/churches/${church.slug}/dashboard`)}
                    >
                      Go to dashboard
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: '.05em',
                        textTransform: 'uppercase',
                        color: 'var(--muted)',
                        marginBottom: 20,
                      }}
                    >
                      Claiming takes about 2 minutes
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 18,
                        marginBottom: 26,
                      }}
                    >
                      {[
                        [
                          "Confirm it's you",
                          `A quick verification that you represent ${churchShort}.`,
                        ],
                        [
                          'Your events go official',
                          'The "unverified" label comes off every listing instantly.',
                        ],
                        [
                          'Turn on RSVPs & sync your calendar',
                          'Manage everything, import from Google Calendar.',
                        ],
                      ].map(([title, desc], i) => (
                        <div
                          key={title}
                          style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}
                        >
                          <span
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 999,
                              background: 'var(--brand-gradient)',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: 13,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flex: '0 0 auto',
                            }}
                          >
                            {i + 1}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 15.5 }}>{title}</div>
                            <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                              {desc}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {claim.isError && (
                      <div
                        role="alert"
                        style={{
                          padding: '10px 14px',
                          borderRadius: 10,
                          background: '#fef2f2',
                          border: '1px solid #fca5a5',
                          color: '#b91c1c',
                          fontSize: 13,
                          marginBottom: 14,
                        }}
                      >
                        {(claim.error as Error)?.message ?? 'Something went wrong'}
                      </div>
                    )}

                    {!user ? (
                      // Claiming requires an account — route into the existing
                      // auth flow, returning to this claim page afterward.
                      <Button
                        size="lg"
                        className="w-full"
                        onClick={() => navigate(`/register?next=/churches/${church.id}/claim`)}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>
                          Claim {churchShort}&nbsp;&nbsp;→
                        </span>
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        className="w-full"
                        disabled={claim.isPending}
                        onClick={handleClaim}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {claim.isPending ? 'Claiming…' : <>Claim {churchShort}&nbsp;&nbsp;→</>}
                        </span>
                      </Button>
                    )}
                    <p
                      style={{
                        fontSize: 13,
                        color: 'var(--muted)',
                        textAlign: 'center',
                        margin: '14px 0 0',
                        lineHeight: 1.5,
                      }}
                    >
                      Free to claim. We'll verify by email at your church's domain.
                    </p>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 18, fontSize: 14, color: 'var(--muted)' }}>
                Not your church? {/* TODO(wiring): "Report this listing" has no destination yet. */}
                <Link
                  to="/for-churches"
                  style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
                >
                  Report this listing
                </Link>
              </div>
            </div>
          </div>

          {/* ── RIGHT: the listing being claimed ─────────────────────────── */}
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 14,
              }}
            >
              The listing you're claiming
            </div>
            <div
              style={{
                position: 'relative',
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-soft)',
                overflow: 'hidden',
              }}
            >
              {/* Cover: the church's real photo once it has one; otherwise a real
                  map pinned at its actual (always-geocoded) location, never a
                  fabricated placeholder — most invited churches have no cover
                  photo yet, but every church has a real location. */}
              <div style={{ position: 'relative', height: 210, overflow: 'hidden' }}>
                {church.coverImageUrl ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${church.coverImageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                ) : (
                  <ChurchLocationMap
                    lat={church.locationLat}
                    lng={church.locationLng}
                    label={churchName}
                    height={210}
                  />
                )}
                {/* This page only renders for unclaimed listings — their public
                    "before" state is unverified (RSVPs off), which the footer
                    notice below also states. Show unverified regardless of the
                    raw church.verified flag so the two never contradict. */}
                <span style={{ position: 'absolute', top: 14, right: 14 }}>
                  <StatusPill status="unverified" />
                </span>
              </div>

              {/* Header */}
              <div
                style={{
                  padding: '20px 24px 18px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  borderBottom: '1px solid var(--line-soft)',
                }}
              >
                <div
                  style={{
                    marginTop: -46,
                    border: '3px solid var(--surface)',
                    borderRadius: 999,
                    flex: '0 0 auto',
                    zIndex: 100,
                  }}
                >
                  <Avatar name={churchShort} src={church.logoUrl} size={64} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 21,
                      fontWeight: 700,
                      letterSpacing: '-0.015em',
                      lineHeight: 1.2,
                    }}
                  >
                    {churchName}
                  </div>
                  <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 3 }}>
                    {metaLine}
                  </div>
                </div>
                {/* TODO(wiring): "★ Follow" is presentational on this pre-claim
                    preview. See docs/onboarding-wiring-todo.md (#10). */}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 999,
                    padding: '7px 14px',
                    flex: '0 0 auto',
                  }}
                >
                  ★ Follow
                </span>
              </div>

              {/* Events */}
              <div style={{ padding: '18px 24px 8px' }}>
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 14 }}
                >
                  Upcoming events
                </div>
                {eventsLoading && (
                  <p style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 0 14px' }}>
                    Loading…
                  </p>
                )}
                {!eventsLoading && occurrences.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 0 14px' }}>
                    No upcoming events scheduled yet. Once claimed, {churchShort}'s events will show
                    up here.
                  </p>
                )}
                {occurrences.map((occ, i) => (
                  <div
                    key={occ.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '12px 0',
                      borderBottom:
                        i === occurrences.length - 1 ? 'none' : '1px solid var(--line-soft)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}
                      >
                        <EventTypePill type={occ.event.eventType ?? 'potluck'} />
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{occ.event.title}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                        {formatEventDate(occ.startsAt)} · {formatEventTime(occ.startsAt)}
                      </div>
                    </div>
                    {/* Every event on this page belongs to an unclaimed church, whose
                        public "before" state is unverified with RSVPs off (see the
                        footer notice) — this is a true statement, not a placeholder.
                        TODO(wiring): if a per-event rsvpsOn override ever exists, drive
                        this from it instead. See docs/onboarding-wiring-todo.md (#9). */}
                    <span
                      style={{
                        fontSize: 12,
                        color: 'var(--muted)',
                        background: 'var(--bg-alt)',
                        padding: '5px 11px',
                        borderRadius: 999,
                        flex: '0 0 auto',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      RSVPs off
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer notice */}
              <div
                style={{
                  background: 'var(--uv-claim-bg)',
                  borderTop: '1px solid #f3e3b8',
                  padding: '14px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: 'var(--warning)',
                    flex: '0 0 auto',
                  }}
                />
                <span style={{ fontSize: 13.5, color: 'var(--uv-claim-ink)', lineHeight: 1.45 }}>
                  Until claimed, these events show as unverified and RSVPs stay off. Claim to switch
                  them on.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
